import { Module } from 'vuex'
import { RootState, SalesPerson, User } from '../types'
import { api } from '@/api'
import { pendingAuthCheck } from '@/api/axios'

interface AuthState {
  user: User | null
  salesPerson: SalesPerson | null
  salesPersons: SalesPerson[]
  isAuthenticated: boolean
  isChecking: boolean
}

const auth: Module<AuthState, RootState> = {
  namespaced: true,
  
  state: () => ({
    user: null,
    salesPerson: null,
    salesPersons: [],
    isAuthenticated: false,
    isChecking: false
  }),

  mutations: {
    SET_USER(state, user: User | null) {
      state.user = user
      state.isAuthenticated = !!user
    },
    SET_SALES_PERSON(state, salesPerson: SalesPerson | null) {
      state.salesPerson = salesPerson
    },
    SET_SALES_PERSONS(state, salesPersons: SalesPerson[]) {
      state.salesPersons = salesPersons
    },
    SET_CHECKING(state, isChecking: boolean) {
      state.isChecking = isChecking
    }
  },

  actions: {
    async checkAuth({ commit, state, dispatch }) {
      // Skip if already checking or authenticated
      if (state.isChecking || state.isAuthenticated) {
        return state.isAuthenticated
      }

      commit('SET_CHECKING', true)
      pendingAuthCheck.isCheckingAuth = true
      
      try {
        const user = await api.auth.me()
        commit('SET_USER', user)
        
        // Fetch sales person after successful authentication
        if (user) {
          await dispatch('fetchSalesPerson')
        }
        
        return true
      } catch (error) {
        commit('SET_USER', null)
        commit('SET_SALES_PERSON', null)
        return false
      } finally {
        commit('SET_CHECKING', false)
        pendingAuthCheck.isCheckingAuth = false
      }
    },

    async login({ commit, dispatch }, { email, password }: { email: string; password: string }) {
      commit('SET_CHECKING', true)
      try {
        await api.auth.login(email, password)
        const user = await api.auth.me()
        commit('SET_USER', user)
        
        // Fetch sales person after successful login
        if (user) {
          await dispatch('fetchSalesPerson')
        }
        
        return true
      } catch (error) {
        commit('SET_USER', null)
        commit('SET_SALES_PERSON', null)
        throw error
      } finally {
        commit('SET_CHECKING', false)
      }
    },

    async signup({ commit, dispatch }, { email, password, sales_person_id }: { email: string; password: string; sales_person_id: number }) {
      commit('SET_CHECKING', true)
      try {
        await api.auth.signup(email, password, sales_person_id)
        const user = await api.auth.me()
        commit('SET_USER', user)
        
        // Fetch sales person after successful signup
        if (user) {
          await dispatch('fetchSalesPerson')
        }
        
        return true
      } catch (error) {
        commit('SET_USER', null)
        commit('SET_SALES_PERSON', null)
        throw error
      } finally {
        commit('SET_CHECKING', false)
      }
    },
    
    async fetchSalesPerson({ commit }) {
      try {
        const salesPerson = await api.salesPerson.fetch()
        commit('SET_SALES_PERSON', salesPerson)
        return salesPerson
      } catch (error) {
        commit('SET_SALES_PERSON', null)
      }
    },

    async fetchAllSalesPersons({ commit }) {
      try {
        const salesPersons = await api.salesPerson.fetchAll()
        commit('SET_SALES_PERSONS', salesPersons)
        return salesPersons
      } catch (error) {
        commit('SET_SALES_PERSONS', [])
        throw error
      }
    },

    logout({ commit }) {
      commit('SET_USER', null)
      commit('SET_SALES_PERSON', null)
      commit('SET_SALES_PERSONS', [])
    }
  }
}

export default auth 